import type { SearchFilters, PanelFilters } from '@content-search/domain';
import { hasActiveFilters, emptyPanelFilters, getDefaultDateRange } from '@content-search/utils';
import { ButtonSize, ButtonType, Card, Button } from '@soteria/design-system-ve';
import { useState, useEffect, useCallback } from 'react';

import { ContentTypeFilter } from './ContentTypeFilter';
import { DateRangeFilter } from './DateRangeFilter';
import * as styles from './FilterPanel.css';
import { LabelsFilter } from './LabelsFilter';
import { LanguageFilter } from './LanguageFilter';
import { MetadataFilter } from './MetadataFilter';
import { PolicyFilter } from './PolicyFilter';
import { SamplingFilter } from './SamplingFilter';
import { TopicsFilter } from './TopicsFilter';

interface FilterPanelProps {
  searchFilters: SearchFilters;
  isLoading: boolean;
  isOpen: boolean;
  onApplyOrClearFilters: (filters: SearchFilters) => void;
}

export function FilterPanel({ searchFilters, isLoading, isOpen, onApplyOrClearFilters }: FilterPanelProps) {
  // Manage local panel filters state internally
  const [localPanelFilters, setLocalPanelFilters] = useState(searchFilters.panelFilters);

  // Sync local state when search filters change (e.g., when badges are removed)
  useEffect(() => {
    setLocalPanelFilters(searchFilters.panelFilters);
  }, [searchFilters.panelFilters]);

  // Initialize dates with defaults when panel opens if they're not set
  useEffect(() => {
    if (isOpen && !localPanelFilters.startDate && !localPanelFilters.endDate) {
      const defaultRange = getDefaultDateRange();

      setLocalPanelFilters(prev => ({
        ...prev,
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when panel opens, not when dates change
  }, [isOpen]);

  // Helper function to update local panel filters
  const updateFilters = useCallback((updates: Partial<PanelFilters>) => {
    setLocalPanelFilters(prevFilters => ({
      ...prevFilters,
      ...updates,
    }));
  }, []);

  // Handle apply filters - create complete search filters and notify parent
  const handleApplyFilters = useCallback(() => {
    const finalSearchFilters: SearchFilters = {
      ...searchFilters,
      panelFilters: localPanelFilters,
    };

    onApplyOrClearFilters(finalSearchFilters);
  }, [localPanelFilters, searchFilters, onApplyOrClearFilters]);

  // Handle clear filters - reset to empty and notify parent
  const handleClearFilters = useCallback(() => {
    const clearedSearchFilters: SearchFilters = {
      userId: searchFilters.userId, // Preserve external filter
      subjectId: searchFilters.subjectId, // Preserve external filter
      panelFilters: emptyPanelFilters, // Clear only panel filters
    };

    setLocalPanelFilters(emptyPanelFilters);
    onApplyOrClearFilters(clearedSearchFilters);
  }, [searchFilters, onApplyOrClearFilters]);

  return (
    <div className={styles.filterCardContainer} data-expanded={isOpen}>
      <Card>
        <div className={styles.filterContainer}>
          {/* Responsive filter grid */}
          <div className={styles.filterGrid}>
            {/* Column 1 - Content Type, Language, and Policy */}
            <div className={styles.filterColumn}>
              <ContentTypeFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
              <LanguageFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
              <PolicyFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
            </div>

            {/* Column 2 - Labels and Topics */}
            <div className={styles.filterColumn}>
              <LabelsFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
              <TopicsFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
            </div>

            {/* Column 3 - Sampling and Metadata */}
            <div className={styles.filterColumn}>
              <SamplingFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
              <MetadataFilter isLoading={isLoading} panelFilters={localPanelFilters} updateFilters={updateFilters} />
            </div>

            {/* Column 4 - Date Range */}
            <div className={styles.filterColumn}>
              <DateRangeFilter isLoading={isLoading} isOpen={isOpen} panelFilters={localPanelFilters} updateFilters={updateFilters} />
            </div>
          </div>
        </div>
        {/* Filter Actions */}
        <div className={styles.filterActions}>
          <button className={styles.clearFiltersButton} disabled={!hasActiveFilters() || isLoading} type="button" onClick={handleClearFilters}>
            Clear All Filters
          </button>
          <Button buttonSize={ButtonSize.Default} buttonType={ButtonType.Brand} disabled={isLoading} label="APPLY" onClick={handleApplyFilters} />
        </div>{' '}
      </Card>
    </div>
  );
}
